import type { RefObject } from 'react';
import React, { forwardRef, useState } from 'react';

import { Typography, useMediaQuery, useTheme } from '@mui/material';
import { AspectRatio } from 'react-aspect-ratio';

import { ImageAddIcon } from '@setup/svg-icons/svg-icons';
import { MuiButton, MuiTooltip } from '@shared/ui-kit';
import {
  UploadFileDragContainer,
  UploadFileDragContentWrapper,
  UploadFileDragWrapper,
} from '@shared/ui-kit/media/Attachemts/components/UploadFileContainer/UploadFileContainer.style';

import type { SxProps, Theme } from '@mui/material';

type UploadFileContainerProps = {
  uploadFileButtonId?: string;
  uploadFileInputId?: string;
  handleGetDataFromInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleGetDataFromDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  acceptedFormat: any;
  isFullWidth?: boolean;
  isFullHeight?: boolean;
  isMultipleInput?: boolean;
  size?: 'small' | 'medium';
  isDisabledUploadFile?: boolean;
  sxUploadContainer?: SxProps<Theme>;
  tooltipSettings?: {
    color?: 'dark' | 'light';
    tooltipText: string;
    tooltipWithIcon: boolean;
  };
  heightContainer?: number;
  aspectRatio?: string | number;
};

const aspectRatioStyle = (
  params: Pick<UploadFileContainerProps, 'size' | 'isFullWidth' | 'heightContainer' | 'isFullHeight'>,
) => {
  const { heightContainer, size, isFullWidth, isFullHeight } = params;
  return {
    ...((heightContainer || isFullHeight) && { height: isFullHeight ? '100%' : heightContainer }),
    width: size === 'small' && !isFullWidth ? '195px' : '100%',
    minWidth: isFullWidth ? '100%' : '195px',
  };
};

const UploadFileContainer = forwardRef<HTMLInputElement, UploadFileContainerProps>((props, ref) => {
  const theme = useTheme();
  const [isDragging, setIsDragging] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    uploadFileButtonId,
    uploadFileInputId,
    acceptedFormat,
    handleGetDataFromInputChange,
    handleGetDataFromDrop,
    size = 'small',
    isFullWidth = false,
    isMultipleInput = false,
    isDisabledUploadFile = false,
    sxUploadContainer,
    tooltipSettings,
    heightContainer,
    isFullHeight = false,
    aspectRatio = '4/3',
  } = props;

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    if (isDisabledUploadFile) return;

    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (isDisabledUploadFile) return;

    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (isDisabledUploadFile) return;

    e.preventDefault();
    setIsDragging(false);
    handleGetDataFromDrop(e);
  };

  const onUploadFile = () => {
    if (isDisabledUploadFile) return;
    if (ref) {
      (ref as RefObject<HTMLInputElement>)?.current?.click();
    }
  };

  return (
    <AspectRatio
      ratio={aspectRatio}
      className={'upload-aspectRatio-element'}
      style={aspectRatioStyle({ isFullHeight, heightContainer, size, isFullWidth })}
    >
      <MuiTooltip
        title={tooltipSettings?.tooltipText || ''}
        tooltipWithIcon={tooltipSettings?.tooltipWithIcon || false}
        color={tooltipSettings?.color || 'dark'}
      >
        <UploadFileDragContainer
          onDragEnter={handleDragEnter}
          onDragOver={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          isDisabledUploadFile={isDisabledUploadFile}
          isDragging={isDragging}
          sx={{ ...sxUploadContainer }}
        >
          <UploadFileDragWrapper
            id={uploadFileButtonId || 'uploadFile'}
            isDisabledUploadFile={isDisabledUploadFile}
            onClick={onUploadFile}
          >
            <ImageAddIcon />
            <UploadFileDragContentWrapper>
              <MuiButton
                label="Upload a file"
                size="small"
                variant="text"
                color="primary"
                isDisabled={isDisabledUploadFile}
                isStopPropagation={false}
              />

              {!isMobile && (
                <Typography
                  variant="t12m"
                  sx={{
                    color: isDisabledUploadFile
                      ? theme.palette.case.neutral.n400
                      : theme.palette.case.neutral.n700,
                  }}
                >
                  or drag and drop
                </Typography>
              )}
            </UploadFileDragContentWrapper>
            <Typography
              variant="t12r"
              sx={{
                textAlign: 'center',
                color: theme.palette.case.neutral.n400,
              }}
            >
              PNG, JPG, HEIC up to 40 MB
            </Typography>
          </UploadFileDragWrapper>
          <input
            id={uploadFileInputId || 'uploadFileInput'}
            ref={ref}
            onChange={isDisabledUploadFile ? () => {} : handleGetDataFromInputChange}
            accept={acceptedFormat}
            type="file"
            name="media"
            multiple={isMultipleInput}
            hidden
          />
        </UploadFileDragContainer>
      </MuiTooltip>
    </AspectRatio>
  );
});

export default UploadFileContainer;
